import * as React from 'react'
// libs
import styled from 'styled-components'
import { Helmet } from 'react-helmet-async'
import { useRouter } from 'hooks/useRouter'
import { useMediaQuery } from '@mui/material'
// types
import { Modals } from 'constants/modals'
// constants
import { SCREEN_SIZE } from 'constants/screenSize'
// components
import MainLayoutContainer from 'containers/MainLayout'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
// views
import { LayoutContainerInnerWrapper } from 'themes/views/layouts'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// view components
import RiskCTA from 'components/PageComponents/Risks/Edit/components/CTA'
import RiskLabel from 'components/PageComponents/Risks/Edit/components/RiskLabel'
import RelatedForms from 'components/PageComponents/Risks/Edit/components/RelatedForms'
import AiChatModal from 'components/Modals/AiChatModal'
import TextFieldModal from 'components/Modals/TextFieldModal'
import EditPrefixModal from 'components/Modals/EditPrefixModal'
import AiFieldValueModal from 'components/Modals/AiFieldValueModal'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import RiskTemplateFields from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields'
import AiLinkedFormsModal from 'components/Modals/AiLinkedFormsModal'
import AiMatrixValueModal from 'components/Modals/AiMatrixValueModal'
import MultipleRelatedForm from 'components/PageComponents/Risks/Edit/components/MultipleRelatedForm'
import RiskRecurrenceModal from 'components/Modals/RiskRecurrenceModal'
import FieldRecurrenceModal from 'components/Modals/ReccurenceSettingsModal'
import AuthorizationWarning from 'components/UI/Warnings/AuthorizationWarning'
// hooks
import { useNetworkStatus } from 'hooks/useNetworkStatus'
import { useFormInitialValues } from 'hooks'
import { useParentRelatedForms } from 'hooks/risks/useParentRelatedForms'

const Wrapper = styled.div`
  height: 100%;
  margin-right: -20px;
  scroll-timeline: --rt-scroll block;
  overflow-x: hidden;
  overflow-y: auto;
`

const RiskEdit: React.FC = () => {
  const router = useRouter()

  const { isLoading } = React.useContext(RiskTemplateAPIContext)
  const { risk, isLoading: isRiskLoading, showArchivedForms } = React.useContext(RiskAPIContext)

  const fieldValues = React.useMemo(
    () =>
      risk?.fieldValueMap?.filter((item) => {
        if (showArchivedForms) {
          return item
        }

        return !item.archived
      }) || [],
    [risk?.fieldValueMap, showArchivedForms]
  )

  const { initialValues, isInitialValuesLoaded } = useFormInitialValues(isLoading || isRiskLoading, fieldValues, {
    risk
  })

  const { isLoading: isParentRelatedFormsLoading, parentRisk } = useParentRelatedForms()

  const onLine = useNetworkStatus()
  const isTablet = useMediaQuery(SCREEN_SIZE.TABLET)

  const isMultipleRelatedForm = !!router.query.multiple
  const showLoader = isLoading || isRiskLoading || !isInitialValuesLoaded || isParentRelatedFormsLoading
  const hasAccess = !isRiskLoading && (risk?.id || isMultipleRelatedForm)

  return (
    <>
      <Helmet>
        <title>Skefto - Edit Form</title>
      </Helmet>
      <MainLayoutContainer
        disableScroll
        isLoading={showLoader}
        CTA={
          onLine && hasAccess ? (
            <RiskCTA />
          ) : (
            <Button type='button' layout={BUTTON_LAYOUTS.PURPLE} onClick={() => router.replace('/forms')}>
              Back
            </Button>
          )
        }
        label={!isLoading && !isTablet && <RiskLabel parentRisk={parentRisk} />}>
        <LayoutContainerInnerWrapper offset={20}>
          {!onLine || hasAccess ? (
            <Wrapper data-rt-scroll>
              <RelatedForms parentRisk={parentRisk} />
              {isMultipleRelatedForm ? <MultipleRelatedForm /> : <RiskTemplateFields initialValues={initialValues} />}
            </Wrapper>
          ) : (
            <AuthorizationWarning entityType='form' />
          )}
        </LayoutContainerInnerWrapper>

        <AiChatModal />
        <TextFieldModal />
        <EditPrefixModal />
        <AiFieldValueModal />
        <AiLinkedFormsModal />
        <AiMatrixValueModal />
        <RiskRecurrenceModal />
        <FieldRecurrenceModal />
        <ConfirmDeleteModal modalType={Modals.CONFIRM_DELETE_CHAT_CONVERSATION_MODAL} header={`Delete Conversation`}>
          {`Are you sure you want to delete this conversation?`}
        </ConfirmDeleteModal>
      </MainLayoutContainer>
    </>
  )
}

export default RiskEdit
